import { useQuery } from '@tanstack/react-query';
import {
  getPortalOrder,
  getPortalOrders,
  PortalOrderDetail,
  PortalOrdersResponse,
} from '@api/customer-portal-api';
import { queryKeys } from '@hooks/queryKeys';
import { normalizeLineOptions } from '@lib/lineOptions';

interface UseOrdersInput {
  page: number;
  pageSize: number;
}

export const useOrders = ({ page, pageSize }: UseOrdersInput) =>
  useQuery({
    queryKey: queryKeys.orders.list(page, pageSize),
    queryFn: async (): Promise<PortalOrdersResponse> => {
      const data = await getPortalOrders({ page, page_size: pageSize });
      return {
        orders: Array.isArray(data?.orders) ? data.orders : [],
        total: typeof data?.total === 'number' ? data.total : 0,
        page: data?.page ?? page,
        page_size: data?.page_size ?? pageSize,
      };
    },
    placeholderData: (previousData) => previousData,
  });

export const useOrder = (orderId: string | null) =>
  useQuery({
    queryKey: orderId ? queryKeys.orders.detail(orderId) : ['orders', 'detail', 'none'],
    queryFn: async (): Promise<PortalOrderDetail> => {
      if (!orderId) throw new Error('orderId is required');
      const order = await getPortalOrder(orderId);
      return {
        ...order,
        line_items: order.line_items.map((li) => ({
          ...li,
          selected_options: normalizeLineOptions(li.selected_options),
        })),
      };
    },
    enabled: Boolean(orderId),
  });
